// Notification Tools
// Send notifications via notification_worker (email, telegram, phone)

import { validateRequired } from '../../utils/validation.js'
import { forwardCallByName } from 'workers-common'

// --- Raw implementation (for worker sandbox RPC) ---
export function createNotificationTools({ getWorkerClient }) {
  const sendNotification = async (ctx, args) => {
    validateRequired(args, ['type', 'params'], 'sendNotification')
    const { type, params } = args

    if (typeof params !== 'object') {
      throw new Error('sendNotification: params must be an object')
    }

    const workerClient = getWorkerClient()

    let method
    let rpcParams

    switch (type) {
      case 'email':
        method = 'notifyByEmail'
        validateRequired(params, ['toEmailAddress', 'subject', 'msg'], 'Email notification')
        rpcParams = {
          toEmailAddress: params.toEmailAddress,
          toName: params.toName || '',
          subject: params.subject,
          msg: params.msg,
          priority: params.priority || 'normal',
        }
        break

      case 'telegram':
        method = 'notifyByTgbot'
        validateRequired(params, ['botId', 'chatId', 'text'], 'Telegram notification')
        rpcParams = {
          botId: params.botId,
          chatId: params.chatId,
          text: params.text,
          parseMode: params.parseMode || 'Markdown',
        }
        break

      case 'phone':
        method = 'notifyByPhoneCall'
        validateRequired(params, ['toPhoneNumber', 'message'], 'Phone notification')
        rpcParams = {
          toPhoneNumber: params.toPhoneNumber,
          message: params.message,
          language: params.language || 'en-US',
          voice: params.voice || 'male',
          maxRetries: params.maxRetries || 1,
        }
        break

      default:
        throw new Error(
          `Unknown notification type: ${type}. Supported types: email, telegram, phone`,
        )
    }

    try {
      const result = await forwardCallByName(ctx, workerClient, 'notification-worker', method, [
        rpcParams,
      ])

      return {
        success: true,
        type,
        result,
      }
    } catch (error) {
      throw new Error(`Notification failed: ${error.message}`)
    }
  }

  return { sendNotification }
}

// --- Tool definition (for LLM) ---
export const sendNotificationDefinition = {
  name: 'sendNotification',
  description: 'Send notification via email, telegram, or phone. Set type and params accordingly.',
  parameters: {
    type: 'object',
    properties: {
      type: {
        type: 'string',
        enum: ['email', 'telegram', 'phone'],
        description: 'Notification type: email, telegram, or phone',
      },
      params: {
        type: 'object',
        description:
          'Email: {toEmailAddress, subject, msg}. Telegram: {botId, chatId, text}. Phone: {toPhoneNumber, message}.',
      },
    },
    required: ['type', 'params'],
  },
}

// --- ToolEntry (for ToolRegistry) ---
export function createNotificationToolEntries({ getWorkerClient, workerName = 'myagent-worker' }) {
  return [{
    id: 'communication/sendNotification',
    name: 'sendNotification',
    source: 'builtin',
    description: 'Send notification via email, telegram, or phone.',
    parameters: sendNotificationDefinition.parameters,
    searchText: 'sendNotification\nSend notification email telegram phone message alert notify\n发送通知 邮件 电报 电话 消息 提醒 短信',
    examples: [
      {
        input: 'send an email to user@example.com about the meeting',
        args: { type: 'email', params: { toEmailAddress: 'user@example.com', subject: 'Meeting', msg: 'Meeting reminder' } },
      },
      {
        input: 'send a telegram message',
        args: { type: 'telegram', params: { botId: 'bot123', chatId: 'chat456', text: 'Hello' } },
      },
    ],
    execute: async (ctx, args) => {
      const workerClient = getWorkerClient()
      return forwardCallByName(ctx, workerClient, workerName, 'notification.send', [args])
    },
  }]
}
